<template>
  <div
    class="flex flex-col items-center justify-center w-full pb-20 bg-[#F2F6FA]"
  >
    <section
      class="flex flex-col items-center w-full justify-center xl:w-[1285px]"
    >
      <div
        class="flex flex-col items-center justify-center w-full h-full pb-20"
      >
        <section
          class="flex flex-col items-center w-full justify-center xl:w-[100%]"
        >
          <div
            class="w-full xl:w-[100%] flex justify-center items-center gap-4"
          >
            <div class="w-full bg-white rounded-xl">
              <div
                class="flex flex-col justify-between w-full px-5 py-4 mx-auto"
              >
                <!-- Header -->
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center gap-2">
                    <v-btn
                      icon
                      @click="$router.back()"
                      class="mr-2"
                    >
                      <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <h2 class="text-xl font-semibold">Escalation To Settings</h2>
                  </div>
                  <div class="flex gap-2 justify-end">
                    <v-btn
                      color="primary"
                      @click="showAddManagerDialog = true"
                    >
                      <v-icon left>mdi-account-plus</v-icon>
                      Add Manager
                    </v-btn>
                    <v-btn
                      color="secondary"
                      :disabled="managers.length === 0"
                      @click="openAddGeneralManagerDialog"
                    >
                      <v-icon left>mdi-account-star</v-icon>
                      Add General Manager
                    </v-btn>
                  </div>
                </div>

                <!-- Description -->
                <div class="border-b pb-4 mb-4">
                  <p class="text-sm text-gray-600">
                    Configure escalation phone numbers for each location. These numbers will be used for urgent matters and escalations.
                  </p>
                  
                  <!-- Note Section -->
                  <div class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div class="flex items-start gap-2">
                      <v-icon small color="blue darken-2" class="mt-1">mdi-information</v-icon>
                      <div>
                        <p class="text-sm font-medium text-blue-900 mb-1">Note:</p>
                        <p class="text-sm text-blue-800">
                          These contact numbers will be used exclusively for escalation purposes when Review Targets or Photo Targets are not being met. The designated contact will be notified to take necessary action for underperforming locations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- General Managers Section -->
                <div v-if="generalManagers.length > 0" class="mb-6">
                  <div class="flex items-center justify-between mb-3">
                    <h3 class="text-lg font-medium">General Managers ({{ generalManagers.length }})</h3>
                    <v-btn
                      text
                      color="primary"
                      @click="showGeneralManagersList = !showGeneralManagersList"
                    >
                      <v-icon left>{{ showGeneralManagersList ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      {{ showGeneralManagersList ? 'Hide' : 'Show' }} List
                    </v-btn>
                  </div>
                  
                  <!-- General Managers List (Collapsible) -->
                  <v-expand-transition>
                    <div v-show="showGeneralManagersList">
                      <div class="space-y-3">
                        <div
                          v-for="generalManager in generalManagers"
                          :key="generalManager.id"
                          class="border rounded-lg p-4 bg-blue-50"
                        >
                          <div class="flex items-start justify-between">
                            <div class="flex-1">
                              <div class="flex items-center gap-2 mb-2">
                                <v-icon small color="primary">mdi-account-star</v-icon>
                                <span class="font-medium text-primary">{{ generalManager.name || 'Unnamed General Manager' }}</span>
                              </div>
                              <div class="flex items-center gap-2 mb-2">
                                <v-icon small>mdi-phone</v-icon>
                                <span class="text-sm">{{ generalManager.contact_number }}</span>
                              </div>
                              <div class="flex items-center gap-2 mb-2">
                                <v-icon small>mdi-email</v-icon>
                                <span class="text-sm">{{ generalManager.email }}</span>
                              </div>
                              <div class="flex items-start gap-2">
                                <v-icon small class="mt-1">mdi-account-group</v-icon>
                                <div class="flex-1">
                                  <span class="text-sm text-gray-600">Assigned Managers ({{ generalManager.assigned_managers.length }}):</span>
                                  <div class="mt-1 flex flex-wrap gap-1">
                                    <v-chip
                                      v-for="managerId in generalManager.assigned_managers.slice(0, 3)"
                                      :key="managerId"
                                      x-small
                                      color="primary"
                                      text-color="white"
                                      class="mr-1"
                                    >
                                      {{ getManagerName(managerId) }}
                                    </v-chip>
                                    <v-chip
                                      v-if="generalManager.assigned_managers.length > 3"
                                      x-small
                                      color="gray"
                                    >
                                      +{{ generalManager.assigned_managers.length - 3 }} more
                                    </v-chip>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="flex items-center gap-2 ml-4">
                              <v-btn
                                icon
                                small
                                @click="editGeneralManager(generalManager)"
                              >
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                              <v-btn
                                icon
                                small
                                color="red"
                                @click="confirmDeleteGeneralManager(generalManager)"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>

                <!-- Managers Section with Toggle -->
                <div v-if="managers.length > 0" class="mb-6">
                  <div class="flex items-center justify-between mb-3">
                    <h3 class="text-lg font-medium">Escalation Managers ({{ managers.length }})</h3>
                    <v-btn
                      text
                      color="primary"
                      @click="showManagersList = !showManagersList"
                    >
                      <v-icon left>{{ showManagersList ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      {{ showManagersList ? 'Hide' : 'Show' }} List
                    </v-btn>
                  </div>
                  
                  <!-- Managers List (Collapsible) -->
                  <v-expand-transition>
                    <div v-show="showManagersList">
                      <!-- Manager Search Bar -->
                      <div class="mb-4">
                        <v-text-field
                          v-model="managerSearchQuery"
                          placeholder="Search managers by name, phone, or location..."
                          prepend-inner-icon="mdi-magnify"
                          outlined
                          dense
                          clearable
                          hide-details
                          @input="filterManagers"
                        ></v-text-field>
                      </div>
                      
                      <!-- Filtered and Paginated Managers -->
                      <div class="space-y-3">
                        <div
                          v-for="manager in paginatedManagers"
                          :key="manager.id"
                          class="border rounded-lg p-4 bg-gray-50"
                        >
                          <div class="flex items-start justify-between">
                            <div class="flex-1">
                              <div class="flex items-center gap-2 mb-2">
                                <v-icon small>mdi-account-tie</v-icon>
                                <span class="font-medium">{{ manager.name || 'Unnamed Manager' }}</span>
                              </div>
                              <div class="flex items-center gap-2 mb-2">
                                <v-icon small>mdi-phone</v-icon>
                                <span class="text-sm">{{ manager.contact_number }}</span>
                              </div>
                              <div class="flex items-center gap-2 mb-2">
                                <v-icon small>mdi-email</v-icon>
                                <span class="text-sm">{{ manager.email }}</span>
                              </div>
                              <div class="flex items-start gap-2">
                                <v-icon small class="mt-1">mdi-map-marker-multiple</v-icon>
                                <div class="flex-1">
                                  <span class="text-sm text-gray-600">Assigned Locations ({{ manager.locations.length }}):</span>
                                  <div class="mt-1 flex flex-wrap gap-1">
                                    <v-chip
                                      v-for="location in manager.locations.slice(0, 3)"
                                      :key="location.id"
                                      x-small
                                      class="mr-1"
                                    >
                                      {{ location.name }}
                                    </v-chip>
                                    <v-chip
                                      v-if="manager.locations.length > 3"
                                      x-small
                                      color="gray"
                                    >
                                      +{{ manager.locations.length - 3 }} more
                                    </v-chip>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="flex items-center gap-2 ml-4">
                              <v-btn
                                icon
                                small
                                @click="editManager(manager)"
                              >
                                <v-icon small>mdi-pencil</v-icon>
                              </v-btn>
                              <v-btn
                                icon
                                small
                                color="red"
                                @click="confirmDeleteManager(manager)"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Pagination -->
                      <div v-if="totalManagerPages > 1" class="mt-4 flex justify-center">
                        <v-pagination
                          v-model="currentManagerPage"
                          :length="totalManagerPages"
                          :total-visible="7"
                          circle
                        ></v-pagination>
                      </div>
                      
                      <!-- No Results Message -->
                      <div v-if="filteredManagers.length === 0 && managerSearchQuery" class="text-center py-4 text-gray-500">
                        No managers found matching "{{ managerSearchQuery }}"
                      </div>
                    </div>
                  </v-expand-transition>
                </div>

                <!-- Search Bar -->
                <div class="mb-4">
                  <v-text-field
                    v-model="searchQuery"
                    placeholder="Search locations..."
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @input="filterLocations"
                  ></v-text-field>
                </div>

                <!-- Loading State -->
                <div v-if="isLoading" class="text-center py-8">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    :size="50"
                    :width="5"
                  ></v-progress-circular>
                  <p class="text-gray-600 mt-3">Loading locations...</p>
                </div>

                <!-- No Locations State -->
                <div
                  v-else-if="!isLoading && stores.length === 0"
                  class="text-center py-8"
                >
                  <v-icon size="50" color="grey">mdi-map-marker-off</v-icon>
                  <p class="text-gray-600 mt-3">No locations found</p>
                  <p class="text-sm text-gray-500 mt-1">
                    Please connect your Google Business Profile locations first
                  </p>
                </div>

                <!-- Locations List -->
                <div
                  v-else-if="!isLoading && filteredStores.length > 0"
                  class="space-y-3"
                >
                  <div
                    v-for="store in filteredStores"
                    :key="store.id"
                    class="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div class="flex items-start justify-between">
                      <div class="flex-1">
                        <h3 class="font-medium text-gray-900">
                          {{ store.title || store.name }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">
                          <v-icon small class="mr-1">mdi-map-marker</v-icon>
                          {{ getStoreAddress(store) }}
                        </p>
                      </div>
                      <div class="ml-4 flex items-center gap-2">
                        <v-text-field
                          v-model="store.phoneNumber"
                          type="tel"
                          label="Phone Number"
                          placeholder="+1234567890"
                          outlined
                          dense
                          hide-details
                          :readonly="!isEditMode(store.id)"
                          :disabled="!isEditMode(store.id)"
                          class="phone-input"
                          style="width: 200px"
                          @input="validatePhoneNumber(store)"
                        ></v-text-field>
                        <v-btn
                          icon
                          small
                          @click="toggleEditMode(store)"
                          :color="isEditMode(store.id) ? 'success' : 'primary'"
                          :loading="savingStoreIds.includes(store.id)"
                        >
                          <v-icon small>
                            {{ isEditMode(store.id) ? 'mdi-check' : 'mdi-pencil' }}
                          </v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <!-- Show source of current number -->
                    <div v-if="!isEditMode(store.id)" class="mt-2">
                      <p class="text-xs text-gray-400">
                        <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
                        {{ getPhoneNumberSource(store) }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- No Results State -->
                <div
                  v-else-if="!isLoading && searchQuery && filteredStores.length === 0"
                  class="text-center py-8"
                >
                  <v-icon size="50" color="grey">mdi-magnify-close</v-icon>
                  <p class="text-gray-600 mt-3">
                    No locations found matching "{{ searchQuery }}"
                  </p>
                  <v-btn
                    text
                    color="primary"
                    @click="clearSearch"
                    class="mt-2"
                  >
                    Clear Search
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>

    <!-- Add/Edit Manager Dialog -->
    <v-dialog v-model="showAddManagerDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ editingManager ? 'Edit Manager' : 'Add Manager' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeManagerDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="managerForm" v-model="isFormValid">
            <!-- Name Field -->
            <v-text-field
              v-model="managerForm.name"
              label="Manager Name (Optional)"
              outlined
              dense
              class="mb-4"
            ></v-text-field>

            <!-- Contact Number Field -->
            <v-text-field
              v-model="managerForm.contact_number"
              label="Contact Number"
              type="tel"
              outlined
              dense
              :rules="phoneRules"
              required
              class="mb-4"
              @input="validateManagerPhoneInput"
              @keypress="onlyNumbers"
            ></v-text-field>

            <!-- Email Field -->
            <v-text-field
              v-model="managerForm.email"
              label="Email Address"
              type="email"
              outlined
              dense
              :rules="emailRules"
              required
              class="mb-4"
            ></v-text-field>

            <!-- Locations Selection -->
            <div class="mb-4">
              <label class="text-sm font-medium text-gray-700 mb-2 block">
                Select Locations (At least one required)
              </label>
              
              <!-- Search for locations -->
              <v-text-field
                v-model="locationSearchQuery"
                placeholder="Search locations..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="mb-3"
                @input="filterDialogLocations"
              ></v-text-field>

              <!-- Locations List with Checkboxes -->
              <div class="border rounded-lg p-3" style="max-height: 300px; overflow-y: auto;">
                <div v-if="filteredDialogStores.length === 0" class="text-center py-4 text-gray-500">
                  No locations found
                </div>
                <v-checkbox
                  v-for="store in filteredDialogStores"
                  :key="store.id"
                  v-model="managerForm.selected_locations"
                  :value="store.id"
                  :label="`${store.title || store.name} - ${getStoreAddress(store)}`"
                  dense
                  hide-details
                  class="mb-2"
                ></v-checkbox>
              </div>
              <p v-if="managerForm.selected_locations.length === 0" class="text-red-500 text-sm mt-2">
                Please select at least one location
              </p>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeManagerDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!isFormValid || managerForm.selected_locations.length === 0 || isSavingManager"
            :loading="isSavingManager"
            @click="saveManager"
          >
            {{ editingManager ? 'Update' : 'Add' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this manager? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="deleteManager" :loading="isDeletingManager">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit General Manager Dialog -->
    <v-dialog v-model="showAddGeneralManagerDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ editingGeneralManager ? 'Edit General Manager' : 'Add General Manager' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeGeneralManagerDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="generalManagerFormRef" v-model="isGeneralManagerFormValid">
            <!-- Name Field -->
            <v-text-field
              v-model="generalManagerForm.name"
              label="General Manager Name (Optional)"
              outlined
              dense
              class="mb-4"
            ></v-text-field>

            <!-- Contact Number Field -->
            <v-text-field
              v-model="generalManagerForm.contact_number"
              label="Contact Number"
              type="tel"
              outlined
              dense
              :rules="phoneRules"
              required
              class="mb-4"
              @input="validateGeneralManagerPhoneInput"
              @keypress="onlyNumbers"
            ></v-text-field>

            <!-- Email Field -->
            <v-text-field
              v-model="generalManagerForm.email"
              label="Email Address"
              type="email"
              outlined
              dense
              :rules="emailRules"
              required
              class="mb-4"
            ></v-text-field>

            <!-- Managers Selection -->
            <div class="mb-4">
              <label class="text-sm font-medium text-gray-700 mb-2 block">
                Assign Managers (At least one required)
              </label>
              
              <!-- Search for managers -->
              <v-text-field
                v-model="managerSearchQueryDialog"
                placeholder="Search managers..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="mb-3"
                @input="filterDialogManagers"
              ></v-text-field>

              <!-- Managers List with Checkboxes -->
              <div class="border rounded-lg p-3" style="max-height: 300px; overflow-y: auto;">
                <div v-if="filteredDialogManagers.length === 0" class="text-center py-4 text-gray-500">
                  No managers available. Please add managers first.
                </div>
                <v-checkbox
                  v-for="manager in filteredDialogManagers"
                  :key="manager.id"
                  v-model="generalManagerForm.assigned_managers"
                  :value="manager.id"
                  :label="`${manager.name || 'Unnamed Manager'} - ${manager.contact_number}`"
                  dense
                  hide-details
                  class="mb-2"
                ></v-checkbox>
              </div>
              <p v-if="generalManagerForm.assigned_managers.length === 0" class="text-red-500 text-sm mt-2">
                Please select at least one manager
              </p>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeGeneralManagerDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!isGeneralManagerFormValid || generalManagerForm.assigned_managers.length === 0 || isSavingGeneralManager"
            :loading="isSavingGeneralManager"
            @click="saveGeneralManager"
          >
            {{ editingGeneralManager ? 'Update' : 'Add' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete General Manager Confirmation Dialog -->
    <v-dialog v-model="showDeleteGeneralManagerDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this general manager? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteGeneralManagerDialog = false">Cancel</v-btn>
          <v-btn color="red" text @click="deleteGeneralManager" :loading="isDeletingGeneralManager">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  getDocs, 
  query, 
  where,
  addDoc,
  deleteDoc,
  serverTimestamp 
} from "firebase/firestore";
import { db } from "@/utils/firebase.utils";

export default {
  name: "EscalationToSetting",
  data() {
    return {
      isLoading: false,
      stores: [],
      filteredStores: [],
      searchQuery: "",
      editModeStoreIds: [], // Array of store IDs in edit mode
      savingStoreIds: [], // Array of store IDs being saved
      userPhoneNumber: "", // Default phone number from user document
      phoneNumberSources: {}, // Track the source of each phone number
      
      // Manager related data
      managers: [],
      filteredManagers: [],
      showManagersList: false,
      managerSearchQuery: '',
      currentManagerPage: 1,
      managersPerPage: 5,
      showAddManagerDialog: false,
      editingManager: null,
      isSavingManager: false,
      isDeletingManager: false,
      showDeleteDialog: false,
      managerToDelete: null,
      isFormValid: false,
      managerForm: {
        name: '',
        contact_number: '',
        email: '',
        selected_locations: []
      },
      locationSearchQuery: '',
      filteredDialogStores: [],
      
      // Validation rules
      phoneRules: [
        v => !!v || 'Contact number is required',
        v => (v && v.length >= 10) || 'Contact number must be at least 10 digits',
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid',
        v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Email format is invalid',
      ],
      
      // General Manager related data
      generalManagers: [],
      showGeneralManagersList: false,
      showAddGeneralManagerDialog: false,
      editingGeneralManager: null,
      isSavingGeneralManager: false,
      isDeletingGeneralManager: false,
      showDeleteGeneralManagerDialog: false,
      generalManagerToDelete: null,
      isGeneralManagerFormValid: false,
      generalManagerForm: {
        name: '',
        contact_number: '',
        email: '',
        assigned_managers: []
      },
      managerSearchQueryDialog: '',
      filteredDialogManagers: [],
    };
  },
  computed: {
    currentUID() {
      return this.$store.state.uid || this.$store.getters.currentUID || this.$store.getters.authUser?.uid;
    },
    WID() {
      return this.$store.state.wid || this.$store.getters.currentWID || this.$store.getters.WID;
    },
    globalStores() {
      return this.$store.state.stores || this.$store.getters.stores || this.$store.getters.globalStores || [];
    },
    paginatedManagers() {
      const start = (this.currentManagerPage - 1) * this.managersPerPage;
      const end = start + this.managersPerPage;
      return this.filteredManagers.slice(start, end);
    },
    totalManagerPages() {
      return Math.ceil(this.filteredManagers.length / this.managersPerPage);
    },
  },
  methods: {
    async fetchUserPhoneNumber() {
      try {
        if (!this.currentUID) {
          console.warn("User ID is not available yet");
          return;
        }
        
        const userRef = doc(db, "users", this.currentUID);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          this.userPhoneNumber = userData.phone_number || "";
        }
      } catch (error) {
        console.error("Error fetching user phone number:", error);
      }
    },

    async fetchManagers() {
      try {
        if (!this.currentUID || !this.WID) {
          console.warn("User ID or Workspace ID is not available yet");
          return;
        }
        
        const managersRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "escalation_contact"
        );
        
        const querySnapshot = await getDocs(managersRef);
        this.managers = [];
        
        querySnapshot.forEach((doc) => {
          const managerData = doc.data();
          this.managers.push({
            id: doc.id,
            ...managerData
          });
        });
        
        // Initialize filtered managers
        this.filteredManagers = [...this.managers];
        
        console.log("Managers fetched:", this.managers.length);
      } catch (error) {
        console.error("Error fetching managers:", error);
      }
    },
    
    filterManagers() {
      if (!this.managerSearchQuery) {
        this.filteredManagers = [...this.managers];
        this.currentManagerPage = 1;
        return;
      }
      
      const searchTerm = this.managerSearchQuery.toLowerCase();
      this.filteredManagers = this.managers.filter((manager) => {
        // Search by name
        const nameMatch = (manager.name || 'Unnamed Manager').toLowerCase().includes(searchTerm);
        
        // Search by phone number
        const phoneMatch = (manager.contact_number || '').toLowerCase().includes(searchTerm);
        
        // Search by email
        const emailMatch = (manager.email || '').toLowerCase().includes(searchTerm);
        
        // Search by location names
        const locationMatch = manager.locations.some(loc => 
          (loc.name || '').toLowerCase().includes(searchTerm) ||
          (loc.address || '').toLowerCase().includes(searchTerm)
        );
        
        return nameMatch || phoneMatch || emailMatch || locationMatch;
      });
      
      // Reset to first page when filtering
      this.currentManagerPage = 1;
    },

    async fetchStores() {
      try {
        this.isLoading = true;
        this.stores = [];
        
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          console.warn("User ID or Workspace ID is not available yet");
          return;
        }

        // First fetch the user's default phone number
        await this.fetchUserPhoneNumber();

        // Use a Set to track unique store IDs and prevent duplicates
        const addedStoreIds = new Set();

        // Method 1: Try to get from globalStores first
        if (this.globalStores && this.globalStores.length > 0) {
          console.log("Using globalStores data:", this.globalStores);
          for (const storeData of this.globalStores) {
            const storeId = storeData.id || storeData.wid;
            
            // Skip if already added
            if (addedStoreIds.has(storeId)) {
              continue;
            }
            
            addedStoreIds.add(storeId);
            const phoneData = this.determinePhoneNumber(storeData);
            this.stores.push({
              ...storeData,
              id: storeId,
              wid: storeId,
              phoneNumber: phoneData.number,
              originalPhoneNumber: phoneData.number, // Store original for comparison
            });
            this.phoneNumberSources[storeId] = phoneData.source;
          }
        }

        // Method 2: If no globalStores, fetch directly from Firestore
        if (this.stores.length === 0) {
          console.log("Fetching stores directly from Firestore");
          const workspacesRef = collection(db, "users", this.currentUID, "fly_workspaces");
          const workspacesQuery = query(
            workspacesRef,
            where("storeWorkspace", "==", true),
            where("admin", "==", this.WID)
          );
          
          const querySnapshot = await getDocs(workspacesQuery);
          
          for (const doc of querySnapshot.docs) {
            const storeData = doc.data();
            const storeId = doc.id;
            
            // Skip if already added
            if (addedStoreIds.has(storeId)) {
              continue;
            }
            
            addedStoreIds.add(storeId);
            
            const phoneData = this.determinePhoneNumber(storeData);
            
            // Add to stores with phone number
            this.stores.push({
              ...storeData,
              id: storeId,
              wid: storeId,
              title: storeData.title || storeData.name || storeData.storeLocation,
              name: storeData.name || storeData.title || storeData.storeLocation,
              phoneNumber: phoneData.number,
              originalPhoneNumber: phoneData.number,
              // Include address fields for display
              address: storeData.address || storeData.storeLocation,
              sub_locality: storeData.sub_locality,
              city: storeData.city,
              state: storeData.state,
              zipcode: storeData.zipcode,
              gmbProfile: storeData.gmbProfile,
            });
            this.phoneNumberSources[storeId] = phoneData.source;
          }
        }

        // Method 3: If still no stores, try fetching all workspaces (not just storeWorkspace)
        if (this.stores.length === 0) {
          console.log("Fetching all workspaces as fallback");
          const allWorkspacesRef = collection(db, "users", this.currentUID, "fly_workspaces");
          const allWorkspacesQuery = query(allWorkspacesRef, where("admin", "==", this.WID));
          
          const querySnapshot = await getDocs(allWorkspacesQuery);
          
          for (const doc of querySnapshot.docs) {
            const storeData = doc.data();
            const storeId = doc.id;
            
            // Skip the default workspace or already added stores
            if (storeData.name === "default" || storeId === this.WID || addedStoreIds.has(storeId)) {
              continue;
            }
            
            addedStoreIds.add(storeId);
            
            const phoneData = this.determinePhoneNumber(storeData);
            
            this.stores.push({
              ...storeData,
              id: storeId,
              wid: storeId,
              title: storeData.title || storeData.name || storeData.storeLocation || "Unnamed Location",
              name: storeData.name || storeData.title || storeData.storeLocation || "Unnamed Location",
              phoneNumber: phoneData.number,
              originalPhoneNumber: phoneData.number,
              address: storeData.address || storeData.storeLocation || "",
              sub_locality: storeData.sub_locality,
              city: storeData.city,
              state: storeData.state,
              zipcode: storeData.zipcode,
              gmbProfile: storeData.gmbProfile,
            });
            this.phoneNumberSources[storeId] = phoneData.source;
          }
        }
        
        console.log("Total stores fetched:", this.stores.length);
        this.filteredStores = [...this.stores];
        this.filteredDialogStores = [...this.stores];
        
        // Fetch managers after stores are loaded
        await this.fetchManagers();
      } catch (error) {
        console.error("Error fetching stores:", error);
        this.$alert.show("Failed to load locations");
      } finally {
        this.isLoading = false;
      }
    },

    determinePhoneNumber(storeData) {
      // Priority 1: Check for gmb_weekly_escalation_number
      if (storeData.gmb_weekly_escalation_number) {
        return {
          number: storeData.gmb_weekly_escalation_number,
          source: "Custom escalation number"
        };
      }
      
      // Priority 2: Check for gmbProfile.phoneNumbers.primaryPhone
      if (storeData.gmbProfile?.phoneNumbers?.primaryPhone) {
        return {
          number: storeData.gmbProfile.phoneNumbers.primaryPhone,
          source: "Google Business Profile"
        };
      }
      
      // Priority 3: Use user's phone number
      return {
        number: this.userPhoneNumber || "",
        source: "Account default number"
      };
    },

    getPhoneNumberSource(store) {
      return this.phoneNumberSources[store.id] || "Unknown source";
    },

    filterLocations() {
      if (!this.searchQuery) {
        this.filteredStores = [...this.stores];
        return;
      }
      
      const searchTerm = this.searchQuery.toLowerCase();
      this.filteredStores = this.stores.filter((store) => {
        const title = (store.title || store.name || "").toLowerCase();
        const address = this.getStoreAddress(store).toLowerCase();
        const phone = (store.phoneNumber || "").toLowerCase();
        return title.includes(searchTerm) || address.includes(searchTerm) || phone.includes(searchTerm);
      });
    },

    filterDialogLocations() {
      if (!this.locationSearchQuery) {
        this.filteredDialogStores = [...this.stores];
        return;
      }
      
      const searchTerm = this.locationSearchQuery.toLowerCase();
      this.filteredDialogStores = this.stores.filter((store) => {
        const title = (store.title || store.name || "").toLowerCase();
        const address = this.getStoreAddress(store).toLowerCase();
        return title.includes(searchTerm) || address.includes(searchTerm);
      });
    },

    clearSearch() {
      this.searchQuery = "";
      this.filteredStores = [...this.stores];
    },

    getStoreAddress(store) {
      // Try to get address from different possible fields
      if (store?.gmbProfile?.storefrontAddress) {
        const addr = store.gmbProfile.storefrontAddress;
        const addressParts = [];
        
        if (addr.addressLines && addr.addressLines.length)
          addressParts.push(...addr.addressLines);
        if (addr.locality) addressParts.push(addr.locality);
        if (addr.administrativeArea)
          addressParts.push(addr.administrativeArea);
        if (addr.postalCode) addressParts.push(addr.postalCode);
        
        return addressParts.join(", ");
      } else if (store?.address) {
        return store.address;
      } else if (store?.sub_locality || store?.city) {
        const parts = [];
        if (store.sub_locality) parts.push(store.sub_locality);
        if (store.city) parts.push(store.city);
        return parts.join(", ");
      }
      return "Address not available";
    },

    validatePhoneNumber(store) {
      // Basic phone number validation (you can make this more sophisticated)
      // Remove any non-digit characters except + at the beginning
      const cleaned = store.phoneNumber.replace(/[^\d+]/g, '');
      if (cleaned.startsWith('+')) {
        store.phoneNumber = '+' + cleaned.substring(1).replace(/[^\d]/g, '');
      } else {
        store.phoneNumber = cleaned.replace(/[^\d]/g, '');
      }
    },
    
    onlyNumbers(event) {
      // Allow only numbers, +, and control keys
      const charCode = event.which ? event.which : event.keyCode;
      // Allow backspace, delete, tab, escape, enter, and .
      if ([8, 9, 27, 13, 110].indexOf(charCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (charCode === 65 && event.ctrlKey === true) ||
        (charCode === 67 && event.ctrlKey === true) ||
        (charCode === 86 && event.ctrlKey === true) ||
        (charCode === 88 && event.ctrlKey === true) ||
        // Allow + at the beginning
        (charCode === 43 && event.target.selectionStart === 0)) {
        return true;
      }
      // Ensure that it is a number and stop the keypress
      if ((charCode < 48 || charCode > 57)) {
        event.preventDefault();
        return false;
      }
      return true;
    },
    
    validateManagerPhoneInput() {
      // Remove any non-digit characters except + at the beginning
      const cleaned = this.managerForm.contact_number.replace(/[^\d+]/g, '');
      if (cleaned.startsWith('+')) {
        this.managerForm.contact_number = '+' + cleaned.substring(1).replace(/[^\d]/g, '');
      } else {
        this.managerForm.contact_number = cleaned.replace(/[^\d]/g, '');
      }
    },

    async updateLocationPhoneNumber(store) {
      // Only save if in edit mode and clicking the check button
      if (!this.isEditMode(store.id)) {
        return;
      }
      
      try {
        // Add to saving list
        this.savingStoreIds.push(store.id);
        
        // Validate required fields
        if (!this.currentUID || !this.WID) {
          throw new Error("User ID or Workspace ID is missing");
        }
        
        // Validate phone number is not empty
        if (!store.phoneNumber || store.phoneNumber.trim() === "") {
          throw new Error("Phone number cannot be empty");
        }
        
        // Update the individual workspace document with gmb_weekly_escalation_number
        const storeWorkspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          store.id
        );
        
        await updateDoc(storeWorkspaceRef, {
          gmb_weekly_escalation_number: store.phoneNumber.trim(),
        });
        
        // Update the source to reflect it's now a custom number
        this.phoneNumberSources[store.id] = "Custom escalation number";
        
        // Update original phone number for future comparisons
        store.originalPhoneNumber = store.phoneNumber;
        
        // Remove from edit mode
        this.editModeStoreIds = this.editModeStoreIds.filter(id => id !== store.id);
        
        this.$snackbar.show(`Phone number updated for ${store.title || store.name}`);
      } catch (error) {
        console.error("Error updating phone number:", error);
        this.$alert.show(`Failed to update phone number: ${error.message}`);
        // Revert to original phone number on error
        store.phoneNumber = store.originalPhoneNumber;
      } finally {
        // Remove from saving list
        this.savingStoreIds = this.savingStoreIds.filter(id => id !== store.id);
      }
    },

    isEditMode(storeId) {
      return this.editModeStoreIds.includes(storeId);
    },

    toggleEditMode(store) {
      if (this.isEditMode(store.id)) {
        // If in edit mode, save the changes
        this.updateLocationPhoneNumber(store);
      } else {
        // Enter edit mode
        this.editModeStoreIds.push(store.id);
      }
    },

    // Manager related methods
    closeManagerDialog() {
      this.showAddManagerDialog = false;
      this.editingManager = null;
      this.managerForm = {
        name: '',
        contact_number: '',
        email: '',
        selected_locations: []
      };
      this.locationSearchQuery = '';
      this.filteredDialogStores = [...this.stores];
    },

    editManager(manager) {
      this.editingManager = manager;
      this.managerForm = {
        name: manager.name || '',
        contact_number: manager.contact_number,
        email: manager.email || '',
        selected_locations: manager.locations.map(loc => loc.id)
      };
      this.showAddManagerDialog = true;
      this.filteredDialogStores = [...this.stores];
    },

    confirmDeleteManager(manager) {
      this.managerToDelete = manager;
      this.showDeleteDialog = true;
    },

    async deleteManager() {
      try {
        this.isDeletingManager = true;
        
        if (!this.managerToDelete) return;
        
        const managerRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "escalation_contact",
          this.managerToDelete.id
        );
        
        await deleteDoc(managerRef);
        
        // Remove from local list
        this.managers = this.managers.filter(m => m.id !== this.managerToDelete.id);
        this.filteredManagers = this.filteredManagers.filter(m => m.id !== this.managerToDelete.id);
        
        this.$snackbar.show("Manager deleted successfully");
        this.showDeleteDialog = false;
        this.managerToDelete = null;
      } catch (error) {
        console.error("Error deleting manager:", error);
        this.$alert.show("Failed to delete manager");
      } finally {
        this.isDeletingManager = false;
      }
    },

    async saveManager() {
      try {
        this.isSavingManager = true;
        
        // Validate form
        if (!this.managerForm.contact_number || !this.managerForm.email || this.managerForm.selected_locations.length === 0) {
          this.$alert.show("Please fill in all required fields");
          return;
        }
        
        // Prepare location data
        const locationData = this.managerForm.selected_locations.map(locId => {
          const store = this.stores.find(s => s.id === locId);
          return {
            id: locId,
            name: store?.title || store?.name || 'Unknown Location',
            address: this.getStoreAddress(store)
          };
        });
        
        const managerData = {
          name: this.managerForm.name || null,
          contact_number: this.managerForm.contact_number,
          email: this.managerForm.email,
          locations: locationData,
          created_time: serverTimestamp(),
        };
        
        if (this.editingManager) {
          // Update existing manager
          const managerRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "escalation_contact",
            this.editingManager.id
          );
          
          await updateDoc(managerRef, managerData);
          
          // Update local list
          const index = this.managers.findIndex(m => m.id === this.editingManager.id);
          if (index !== -1) {
            this.managers[index] = {
              ...this.managers[index],
              ...managerData
            };
          }
          
          // Update filtered managers
          const filteredIndex = this.filteredManagers.findIndex(m => m.id === this.editingManager.id);
          if (filteredIndex !== -1) {
            this.filteredManagers[filteredIndex] = {
              ...this.filteredManagers[filteredIndex],
              ...managerData
            };
          }
          
          this.$snackbar.show("Manager updated successfully");
        } else {
          // Add new manager
          const managersRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "escalation_contact"
          );
          
          const docRef = await addDoc(managersRef, managerData);
          
          // Add to local list
          this.managers.push({
            id: docRef.id,
            ...managerData
          });
          
          // Update filtered managers
          this.filteredManagers = [...this.managers];
          
          this.$snackbar.show("Manager added successfully");
        }
        
        this.closeManagerDialog();
      } catch (error) {
        console.error("Error saving manager:", error);
        this.$alert.show("Failed to save manager");
      } finally {
        this.isSavingManager = false;
      }
    },

    // General Manager Methods
    async fetchGeneralManagers() {
      try {
        if (!this.currentUID || !this.WID) {
          console.warn("User ID or Workspace ID is not available yet");
          return;
        }
        
        const generalManagersRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "general_managers"
        );
        
        const querySnapshot = await getDocs(generalManagersRef);
        this.generalManagers = [];
        
        querySnapshot.forEach((doc) => {
          const generalManagerData = doc.data();
          this.generalManagers.push({
            id: doc.id,
            ...generalManagerData
          });
        });
        
        console.log("General Managers fetched:", this.generalManagers.length);
      } catch (error) {
        console.error("Error fetching general managers:", error);
      }
    },

    async openAddGeneralManagerDialog() {
      console.log("Opening General Manager Dialog. Managers available:", this.managers);
      
      // If managers haven't been loaded yet, fetch them
      if (this.managers.length === 0) {
        await this.fetchManagers();
      }
      
      this.editingGeneralManager = null;
      this.generalManagerForm = {
        name: '',
        contact_number: '',
        email: '',
        assigned_managers: []
      };
      this.managerSearchQueryDialog = '';
      // Ensure managers are copied to filteredDialogManagers
      this.filteredDialogManagers = this.managers ? [...this.managers] : [];
      console.log("Filtered Dialog Managers after ensuring fetch:", this.filteredDialogManagers);
      this.showAddGeneralManagerDialog = true;
    },

    editGeneralManager(generalManager) {
      console.log("Editing General Manager. Managers available:", this.managers);
      this.editingGeneralManager = generalManager;
      this.generalManagerForm = {
        name: generalManager.name || '',
        contact_number: generalManager.contact_number || '',
        email: generalManager.email || '',
        assigned_managers: [...(generalManager.assigned_managers || [])]
      };
      this.managerSearchQueryDialog = '';
      // Ensure managers are copied to filteredDialogManagers
      this.filteredDialogManagers = this.managers ? [...this.managers] : [];
      console.log("Filtered Dialog Managers:", this.filteredDialogManagers);
      this.showAddGeneralManagerDialog = true;
    },

    closeGeneralManagerDialog() {
      this.showAddGeneralManagerDialog = false;
      this.editingGeneralManager = null;
      this.generalManagerForm = {
        name: '',
        contact_number: '',
        email: '',
        assigned_managers: []
      };
      this.managerSearchQueryDialog = '';
      this.$refs.generalManagerFormRef?.resetValidation();
    },

    confirmDeleteGeneralManager(generalManager) {
      this.generalManagerToDelete = generalManager;
      this.showDeleteGeneralManagerDialog = true;
    },

    async deleteGeneralManager() {
      try {
        this.isDeletingGeneralManager = true;
        
        const generalManagerRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "general_managers",
          this.generalManagerToDelete.id
        );
        
        await deleteDoc(generalManagerRef);
        
        // Remove from local list
        this.generalManagers = this.generalManagers.filter(gm => gm.id !== this.generalManagerToDelete.id);
        
        this.$snackbar.show("General Manager deleted successfully");
        this.showDeleteGeneralManagerDialog = false;
        this.generalManagerToDelete = null;
      } catch (error) {
        console.error("Error deleting general manager:", error);
        this.$alert.show("Failed to delete general manager");
      } finally {
        this.isDeletingGeneralManager = false;
      }
    },

    async saveGeneralManager() {
      try {
        this.isSavingGeneralManager = true;
        
        // Validate form
        if (!this.generalManagerForm.contact_number || !this.generalManagerForm.email || this.generalManagerForm.assigned_managers.length === 0) {
          this.$alert.show("Please fill in all required fields and assign at least one manager");
          return;
        }
        
        const generalManagerData = {
          name: this.generalManagerForm.name || null,
          contact_number: this.generalManagerForm.contact_number,
          email: this.generalManagerForm.email,
          assigned_managers: this.generalManagerForm.assigned_managers,
          created_time: serverTimestamp(),
        };
        
        if (this.editingGeneralManager) {
          // Update existing general manager
          const generalManagerRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "general_managers",
            this.editingGeneralManager.id
          );
          
          await updateDoc(generalManagerRef, generalManagerData);
          
          // Update local list
          const index = this.generalManagers.findIndex(gm => gm.id === this.editingGeneralManager.id);
          if (index !== -1) {
            this.generalManagers[index] = {
              ...this.generalManagers[index],
              ...generalManagerData
            };
          }
          
          this.$snackbar.show("General Manager updated successfully");
        } else {
          // Add new general manager
          const generalManagersRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "general_managers"
          );
          
          const docRef = await addDoc(generalManagersRef, generalManagerData);
          
          // Add to local list
          this.generalManagers.push({
            id: docRef.id,
            ...generalManagerData
          });
          
          this.$snackbar.show("General Manager added successfully");
        }
        
        this.closeGeneralManagerDialog();
      } catch (error) {
        console.error("Error saving general manager:", error);
        this.$alert.show("Failed to save general manager");
      } finally {
        this.isSavingGeneralManager = false;
      }
    },

    filterDialogManagers() {
      if (!this.managerSearchQueryDialog) {
        this.filteredDialogManagers = [...this.managers];
        return;
      }
      
      const searchTerm = this.managerSearchQueryDialog.toLowerCase();
      this.filteredDialogManagers = this.managers.filter((manager) => {
        const nameMatch = (manager.name || 'Unnamed Manager').toLowerCase().includes(searchTerm);
        const phoneMatch = (manager.contact_number || '').toLowerCase().includes(searchTerm);
        const emailMatch = (manager.email || '').toLowerCase().includes(searchTerm);
        
        return nameMatch || phoneMatch || emailMatch;
      });
    },

    getManagerName(managerId) {
      const manager = this.managers.find(m => m.id === managerId);
      return manager ? (manager.name || 'Unnamed Manager') : 'Unknown Manager';
    },

    validateGeneralManagerPhoneInput(event) {
      // Only allow numbers
      const char = String.fromCharCode(event.keyCode);
      if (!/[0-9]/.test(char)) {
        event.preventDefault();
      }
    },
  },
  async mounted() {
    // Wait for Vuex store to be fully initialized
    let retryCount = 0;
    const maxRetries = 10;
    
    while ((!this.currentUID || !this.WID) && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 200));
      retryCount++;
    }
    
    if (!this.currentUID || !this.WID) {
      console.error("Failed to get User ID or Workspace ID after retries");
      this.$alert.show("Failed to load user information. Please refresh the page.");
      return;
    }
    
    console.log("Component mounted with UID:", this.currentUID, "WID:", this.WID);
    
    // Fetch all data in parallel
    await Promise.all([
      this.fetchStores(),
      this.fetchManagers(),
      this.fetchGeneralManagers()
    ]);
  },
  watch: {
    globalStores: {
      immediate: false,
      handler(newStores, oldStores) {
        // Only fetch if there's actually new data and we haven't fetched yet
        const hasNewData = newStores && newStores.length > 0;
        const isDataChanged = JSON.stringify(newStores) !== JSON.stringify(oldStores);
        
        if (hasNewData && isDataChanged && !this.isLoading) {
          console.log("GlobalStores updated with new data:", newStores?.length || 0, "stores");
          // Only fetch if we don't have stores yet or if the data actually changed
          if (this.stores.length === 0 || (oldStores && oldStores.length !== newStores.length)) {
            this.fetchStores();
          }
        }
      },
    },
    currentUID: {
      handler(newVal, oldVal) {
        // Only trigger if value actually changed and we have all required data
        if (newVal && newVal !== oldVal && this.WID) {
          if (this.stores.length === 0 && !this.isLoading) {
            this.fetchStores();
          }
          if (this.managers.length === 0) {
            this.fetchManagers();
          }
          if (this.generalManagers.length === 0) {
            this.fetchGeneralManagers();
          }
        }
      },
    },
    WID: {
      handler(newVal, oldVal) {
        // Only trigger if value actually changed and we have all required data
        if (newVal && newVal !== oldVal && this.currentUID) {
          if (this.stores.length === 0 && !this.isLoading) {
            this.fetchStores();
          }
          if (this.managers.length === 0) {
            this.fetchManagers();
          }
          if (this.generalManagers.length === 0) {
            this.fetchGeneralManagers();
          }
        }
      },
    },
    showAddGeneralManagerDialog: {
      handler(newVal) {
        // When dialog opens, ensure filteredDialogManagers is populated
        if (newVal && this.managers.length > 0) {
          this.filteredDialogManagers = [...this.managers];
          console.log("Dialog opened, setting filteredDialogManagers:", this.filteredDialogManagers);
        }
      },
    },
  },
};
</script>

<style scoped>
.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.phone-input >>> .v-input__control {
  min-height: 38px !important;
}

.phone-input >>> .v-text-field input {
  padding: 8px 12px !important;
}

/* Disabled state styling */
.phone-input >>> .v-text-field--disabled input {
  color: rgba(0, 0, 0, 0.6) !important;
  background-color: #f5f5f5 !important;
}

/* Readonly state styling */
.phone-input >>> .v-text-field input[readonly] {
  background-color: #fafafa !important;
}
</style>